import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users, Search, FileText, UserPlus, Plus } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import StatCard from "@/components/dashboard/StatCard";
import UrgentBanner from "@/components/dashboard/UrgentBanner";
import ActivityFeed from "@/components/dashboard/ActivityFeed";
import DebtorRowCard from "@/components/dashboard/DebtorRowCard";
import ProDebtorTable from "@/components/dashboard/ProDebtorTable";
import TierChangeModal from "@/components/jea/TierChangeModal";
import RunQueryModal from "@/components/queries/RunQueryModal";
import { apiClient, formatApiError } from "@/lib/apiClient";
import PillButton from "@/components/jea/PillButton";
import Skeleton from "@/components/jea/Skeleton";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { useRolePermissions } from "@/hooks/useRolePermissions";

const MODE_KEY = "jea_pro_mode";

const ProDashboard = ({ user }) => {
  const nav = useNavigate();
  const { guardAction } = usePreviewGate();
  const { canMutateDebtors, canRunQueries } = useRolePermissions();
  const handleAddDebtor = guardAction(() => nav("/debtors/new"), { feature: "adding judgments" });
  const [searchParams, setSearchParams] = useSearchParams();
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urgent, setUrgent] = useState(null);
  const [tierModal, setTierModal] = useState({ open: false, debtor: null, submitting: false });
  const [runQueryState, setRunQueryState] = useState({ open: false, debtor: null });
  const [stats, setStats] = useState({ runs_this_month: 0, reports_total: 0 });

  const loadStats = async () => {
    try {
      const { data } = await apiClient.get("/queries/stats");
      setStats(data);
    } catch { /* ignore */ }
  };

  const initialMode = useMemo(() => {
    const q = searchParams.get("mode");
    if (q === "guided" || q === "advanced") return q;
    try {
      const saved = localStorage.getItem(MODE_KEY);
      if (saved === "guided" || saved === "advanced") return saved;
    } catch {}
    return "advanced";
  }, [searchParams]);
  const [mode, setMode] = useState(initialMode);

  useEffect(() => {
    try { localStorage.setItem(MODE_KEY, mode); } catch {}
    if (searchParams.get("mode") && searchParams.get("mode") !== mode) {
      const sp = new URLSearchParams(searchParams);
      sp.set("mode", mode);
      setSearchParams(sp, { replace: true });
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/debtors");
      setDebtors(data.debtors || []);
      // Urgent sweep — parallel critical-dates calls
      const active = (data.debtors || []).filter((d) => d.status !== "archived");
      const urgentList = [];
      await Promise.all(active.map(async (d) => {
        try {
          const { data: cd } = await apiClient.get(`/debtors/${d.id}/critical-dates`);
          const je = cd?.dates?.judgment_expires?.days_until;
          if (je != null && je >= 0 && je <= 30) {
            urgentList.push({ debtor_id: d.id, name: d.full_name, days_until: je });
          }
        } catch {}
      }));
      urgentList.sort((a, b) => a.days_until - b.days_until);
      setUrgent(urgentList.length ? { ...urgentList[0], total_urgent: urgentList.length } : null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadStats(); }, []);

  const activeDebtors = debtors.filter((d) => d.status !== "archived");
  const openTier = (d) => setTierModal({ open: true, debtor: d, submitting: false });
  const confirmTier = async (newTier) => {
    if (!tierModal.debtor) return;
    setTierModal((t) => ({ ...t, submitting: true }));
    try {
      await apiClient.patch(`/debtors/${tierModal.debtor.id}`, { tier: newTier });
      toast.success("Tier updated.");
      setTierModal({ open: false, debtor: null, submitting: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setTierModal((t) => ({ ...t, submitting: false }));
    }
  };

  const runQuery = () => toast.info("Please open a debtor to run a broker query.");
  const openRunQuery = (d) => setRunQueryState({ open: true, debtor: d });
  const closeRunQuery = () => setRunQueryState({ open: false, debtor: null });
  const onQueryDone = (result) => {
    closeRunQuery();
    loadStats();
    if (result?.query_run?.id && result?.query_run?.debtor_id) {
      nav(`/debtors/${result.query_run.debtor_id}/queries/${result.query_run.id}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="pro-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="space-y-1">
          <div className="jea-caption">Command Center</div>
          <h1 className="text-[24px] font-bold text-white">PRO Dashboard</h1>
        </div>
        <div className="flex gap-2" data-testid="pro-mode-toggle">
          <PillButton
            active={mode === "guided"}
            onClick={() => setMode("guided")}
            data-testid="mode-guided"
          >
            Guided Mode
          </PillButton>
          <PillButton
            active={mode === "advanced"}
            onClick={() => setMode("advanced")}
            data-testid="mode-advanced"
          >
            Advanced Mode
          </PillButton>
        </div>
      </div>

      {urgent && <UrgentBanner urgent={urgent} />}

      <div className="grid grid-cols-4 gap-4" data-testid="pro-stats">
        <StatCard label="Total Judgments" value={activeDebtors.length} Icon={Users} loading={loading} testid="stat-total-debtors" />
        <StatCard label="Queries This Month" value={stats.runs_this_month} Icon={Search} loading={loading} testid="stat-queries-month" />
        <StatCard label="Reports Generated" value={stats.reports_total} Icon={FileText} loading={loading} testid="stat-reports-generated" />
        <StatCard label="Team Members" value={1} Icon={UserPlus} loading={loading} testid="stat-team-members" />
      </div>

      {mode === "advanced" ? (
        <div data-testid="pro-advanced-view">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-semibold text-[18px]">All Judgments</h2>
            {canMutateDebtors && (
              <JeaButton onClick={handleAddDebtor} className="gap-2" data-testid="pro-dashboard-add-debtor">
                <Plus size={16} /> Add New Judgment
              </JeaButton>
            )}
          </div>
          {loading ? (
            <div className="bg-bg-secondary border border-border-default rounded-[12px] p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : (
            <ProDebtorTable debtors={debtors} onArchived={load} onRunQuery={openRunQuery} onViewReports={(d) => nav(`/debtors/${d.id}?tab=reports`)} />
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_320px] gap-6" data-testid="pro-guided-view">
          <div className="min-w-0 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-semibold text-[18px]">All Judgments</h2>
              {canMutateDebtors && (
                <JeaButton onClick={handleAddDebtor} className="gap-2" data-testid="pro-guided-add-debtor">
                  <Plus size={16} /> Add New Judgment
                </JeaButton>
              )}
            </div>
            {loading ? (
              <Skeleton className="h-32" />
            ) : activeDebtors.length === 0 ? (
              <div className="text-text-secondary text-[14px]">No judgments yet.</div>
            ) : (
              activeDebtors.map((d) => (
                <DebtorRowCard
                  key={d.id}
                  debtor={d}
                  onOpen={() => nav(`/debtors/${d.id}`)}
                  onRunQuery={canRunQueries ? () => openRunQuery(d) : null}
                  onViewReports={() => nav(`/debtors/${d.id}?tab=reports`)}
                  onEditTier={canMutateDebtors ? () => openTier(d) : null}
                />
              ))
            )}
          </div>
          <ActivityFeed />
        </div>
      )}

      <TierChangeModal
        open={tierModal.open}
        currentTier={tierModal.debtor?.tier}
        submitting={tierModal.submitting}
        onCancel={() => setTierModal({ open: false, debtor: null, submitting: false })}
        onConfirm={confirmTier}
      />

      <RunQueryModal
        open={runQueryState.open}
        debtor={runQueryState.debtor}
        onClose={closeRunQuery}
        onCompleted={onQueryDone}
      />
    </div>
  );
};

export default ProDashboard;
